import isEmpty from 'lodash/isEmpty';
import type { LanguageCode } from '../dto/asyncOperationItemCreationTypes';
// The ISO-639-2 codes corresponding to the languages listed in
// https://www.textrazor.com/languages
export type TextRazorCode = 'chi' | 'dut' | 'eng' | 'fre' | 'ger' | 'ita' | 'jpn' | 'pol' | 'por' | 'rus' | 'spa' | 'swe';


// The codes for languages supported by Amazon transcribe
// See https://docs.aws.amazon.com/transcribe/latest/dg/what-is-transcribe.html
export type AwsTranscribeCode =
  'ar-SA' | 'af-ZA' | 'ar-AE' | 'da-DK' | 'de-CH' | 'de-DE' | 'en-AB' | 'en-AU' | 'en-GB' | 'en-IE' | 'en-IN' | 'en-NZ' |
  'en-US' | 'en-WL' | 'en-ZA' | 'es-ES' | 'es-US' | 'fa-IR' | 'fr-CA' | 'fr-FR' | 'he-IL' | 'hi-IN' | 'id-ID' | 'it-IT' |
  'ja-JP' | 'ko-KR' | 'ms-MY' | 'nl-NL' | 'pt-BR' | 'pt-PT' | 'ru-RU' | 'ta-IN' | 'te-IN' | 'th-TH' | 'tr-TR' | 'zh-CN' |
  'zh-TW' | 'auto';


// The BCP-47 values listed in https://cloud.google.com/speech-to-text/docs/languages
export type GoogleSpeechToTextCode =
  'af-ZA' | 'am-ET' | 'ar-AE' | 'ar-BH' | 'ar-DZ' | 'ar-EG' | 'ar-IL' | 'ar-IQ' | 'ar-JO' | 'ar-KW' | 'ar-LB' | 'ar-MA' |
  'ar-OM' | 'ar-QA' | 'ar-PS' | 'ar-SA' | 'ar-TN' | 'az-AZ' | 'bg-BG' | 'bn-BD' | 'bn-IN' | 'ca-ES' | 'cs-CZ' | 'da-DK' |
  'de-DE' | 'el-GR' | 'en-AU' | 'en-CA' | 'en-GB' | 'en-GH' | 'en-IE' | 'en-IN' | 'en-KE' | 'en-NG' | 'en-NZ' | 'en-PH' |
  'en-SG' | 'en-TZ' | 'en-US' | 'en-ZA' | 'es-AR' | 'es-BO' | 'es-CL' | 'es-CO' | 'es-CR' | 'es-DO' | 'es-EC' | 'es-ES' |
  'es-GT' | 'es-HN' | 'es-MX' | 'es-NI' | 'es-PA' | 'es-PE' | 'es-PR' | 'es-PY' | 'es-SV' | 'es-US' | 'es-UY' | 'es-VE' |
  'eu-ES' | 'fa-IR' | 'fi-FI' | 'fil-PH' |
  'fr-CA' | 'fr-FR' | 'gl-ES' | 'gu-IN' | 'he-IL' | 'hi-IN' | 'hr-HR' | 'hu-HU' | 'hy-AM' | 'id-ID' | 'is-IS' | 'it-IT' |
  'ja-JP' | 'jv-ID' | 'ka-GE' | 'km-KH' | 'kn-IN' | 'ko-KR' | 'lo-LA' | 'lt-LT' | 'lv-LV' | 'ml-IN' | 'mr-IN' | 'ms-MY' |
  'nb-NO' | 'ne-NP' | 'nl-NL' | 'pl-PL' | 'pt-BR' | 'pt-PT' | 'ro-RO' | 'ru-RU' | 'si-LK' | 'sk-SK' | 'sl-SI' | 'sr-RS' |
  'su-ID' | 'sv-SE' | 'sw-KE' | 'sw-TZ' | 'ta-IN' | 'ta-LK' | 'ta-MY' | 'ta-SG' | 'te-IN' | 'th-TH' | 'tr-TR' | 'uk-UA' |
  'ur-IN' | 'ur-PK' | 'vi-VN' | 'yue-Hant-HK'     | 'zh'    | 'zh-HK' | 'zh-TW' | 'zu-ZA' | 'sq-AL' | 'ar-YE' | 'bs-BA' |
  'my-MM' | 'en-HK' | 'en-PK' | 'nl-BE' | 'fr-BE' | 'fr-CH' | 'de-CH' | 'de-AT' | 'it-CH' | 'kk-KZ' | 'mk-MK' | 'mn-MN' |
  'et-EE' | 'uz-UZ' | 'pa-Guru-IN';


// The values listed in the "Broadband model" column of the table available at:
//
//   https://cloud.ibm.com/docs/services/speech-to-text?topic=speech-to-text-models#modelsList
//
// but without the "_BroadbandModel" suffix and excluding the languages marked as "Beta"
export type IbmSpeechToTextCode =
  'ar-AR' | 'de-DE' | 'en-GB' | 'en-US' | 'es-GB' | 'es-ES' | 'fr-FR'| 'ja-JP' | 'ko-KR' | 'pt-BR' | 'zh-CN';


// Here are the values that can be passed to the 'language' parameter passed to the analyze requests
// (see https://cloud.ibm.com/apidocs/natural-language-understanding#analyze-text and
// https://cloud.ibm.com/docs/services/natural-language-understanding?topic=natural-language-understanding-language-support)
// Only the ones supported for "Categories" feature are added for text analysis.
// The values must be valid ISO 639-1 codes
export type IbmNluCode = 'ar' | 'de' | 'es' | 'en' | 'fr' | 'it' | 'ja' | 'ko' | 'nl' | 'pt' | 'zh';


// The language codes supported by the Speech Services listed at:
// https://docs.microsoft.com/en-us/azure/cognitive-services/speech-service/language-support
export type MsSpeechToTextCode =
  'ar-EG' | 'ar-BH' | 'ar-JO' | 'ar-LB' | 'ar-SA' | 'ar-SY' | 'ca-ES' | 'da-DK' | 'de-DE' | 'en-AU' | 'en-CA' |
  'en-GB' | 'en-IN' | 'en-NZ' | 'en-US' | 'es-ES' | 'es-MX' | 'fr-CA' | 'fr-FR' | 'fi-FI' | 'hi-IN' | 'it-IT' |
  'ja-JP' | 'ko-KR' | 'nb-NO' | 'nl-NL' | 'pt-BR' | 'pt-PT' | 'ru-RU' | 'sv-SE' | 'th-TH' | 'zh-CN' | 'zh-HK' |
  'zh-TW' | 'ar-AE' | 'ar-IL' | 'ar-IQ' | 'ar-KW' | 'ar-OM' | 'ar-PS' | 'ar-QA' | 'bg-BG' | 'cs-CZ' | 'de-AT' |
  'el-GR' | 'en-GH' | 'en-HK' | 'en-IE' | 'en-KE' | 'en-NG' | 'en-PH' | 'en-SG' | 'en-TZ' | 'en-ZA' | 'es-AR' |
  'es-BO' | 'es-CL' | 'es-CO' | 'es-CR' | 'es-CU' | 'es-DO' | 'es-EC' | 'es-GQ' | 'es-GT' | 'es-HN' | 'es-NI' |
  'es-PA' | 'es-PE' | 'es-PR' | 'es-PY' | 'es-SV' | 'es-US' | 'es-UY' | 'es-VE' | 'et-EE' | 'vi-VN' | 'fr-CH' |
  'ga-IE' | 'gu-IN' | 'hr-HR' | 'hu-HU' | 'id-ID' | 'lt-LT' | 'lv-LV' | 'mr-IN' | 'ms-MY' | 'mt-MT' | 'pl-PL' |
  'ro-RO' | 'sk-SK' | 'sl-SI' | 'ta-IN' | 'te-IN' | 'tr-TR' | 'fil-PH'| 'is-IS' | 'uk-UA' | 'af-ZA' | 'am-ET' |
  'ar-DZ' | 'ar-LY' | 'ar-MA' | 'ar-TN' | 'ar-YE' | 'my-MM' | 'nl-BE' | 'fr-BE' | 'de-CH' | 'he-IL' | 'jv-ID' |
  'kn-IN' | 'km-KH' | 'lo-LA' | 'mk-MK' | 'fa-IR' | 'sr-RS' | 'si-LK' | 'sw-KE' | 'sw-TZ' | 'uz-UZ' | 'zu-ZA' |
  'kk-KZ' | 'cy-GB';

// The supported language codes listed at:
// https://apidocs.zoommedia.ai/#languages
export type ZoomSpeechToTextCode = 'da-dk' | 'en-us' | 'nb-no' | 'nl-be' | 'nl-nl' | 'sv-se';

// https://smbatchdocs.z6.web.core.windows.net/overview.html
export type SpeechmaticsSpeechToTextCode =
  'ar' | 'ba' | 'be' | 'bg' | 'ca' | 'cmn' | 'cs' | 'cy' | 'da' | 'de' | 'el' | 'en' | 'eo' | 'es' | 'et' | 'eu' | 'fi' | 'fr' | 'gl' | 'hi' | 'hr' | 'hu' |
  'ia' | 'it' | 'ja' | 'ko' | 'lt' | 'lv' | 'mr' | 'mn' | 'nl' | 'no' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sv' | 'ta' | 'th' | 'tr' | 'ug' | 'vi' | 'auto';

// https://dev.trint.com/reference#upload
export type TrintSpeechToTextCode =
  'en-GB' | 'en' | 'es' | 'de' | 'ar' | 'bg' | 'ca' | 'cmn' | 'hr' | 'cs' | 'da' | 'nl' | 'fi' | 'fr' |'el' | 'hi' |
  'hu'    | 'it' | 'ja' | 'ko' | 'lv' | 'lt' | 'ms' | 'no' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sv' | 'tr' |
  'id'    | 'uk' | 'yue' | 'cy' | 'ba' | 'eu' | 'be' | 'et' | 'gl' | 'mr' | 'mn' | 'ta'| 'th' | 'ug' | 'vi' | 'auto';

// https://amberscript.github.io/api-docs/#language-codes
export type AmberscriptSpeechToTextCode =
  'af-za' | 'sq-al' | 'am-et' | 'ar' | 'hy-am' | 'az-az' | 'id-id' | 'eu-es' | 'bn-bd' | 'bn-in' | 'bs-ba' | 'bg' | 'my-mm' |
  'ca' | 'cmn' | 'hr' | 'cs' | 'da' | 'nl' | 'en-au' | 'en-uk' | 'en' | 'et-ee' | 'fa-ir' | 'fil-ph' | 'fi' | 'nl-be' | 'fr-ca' |
  'fr' | 'gl-es' | 'ka-ge' | 'de-at' | 'de-ch' | 'de' | 'el' | 'gu-in' | 'iw-il' | 'hi' | 'hu' | 'is-is' | 'it' | 'ja' | 'jv-id' |
  'kn-in' | 'km-kh' | 'ko' | 'lo-la' | 'lv' | 'lt' | 'mk-mk' | 'ms' | 'ml-in' | 'mr-in' | 'mn-mn' | 'ne-np' | 'no' | 'pl' | 'pt-br' |
  'pt' | 'pa-guru-in' | 'ro' | 'ru' | 'sr-rs' | 'si-lk' | 'sk' | 'sl' | 'es' | 'su-id' | 'sw-ke' | 'sw-tz' | 'sv' | 'ta-in' | 'ta-my' |
  'ta-sg' | 'ta-lk' | 'te-in' | 'th-th' | 'tr' | 'uk-ua' | 'ur-in' | 'ur-pk' | 'uz-uz' | 'vi-vn' | 'zu-za';

// https://github.com/Mjoll/mimir/issues/466
export type RecappSpeechToTextCode = 'gech' | 'fr';

// As per documentation (https://mimir.recapp.ch/api/), it supports 15 different languages:
//   deu-CH, deu-DE, fra-CH, gsw-CH, gsw-CH_8k, gsw-VS, fradeu-auto, ita-CH,
//   roh-rumgr, roh-sursilv, roh-sutsilv, roh-surmiran, roh-puter, roh-vallader, roh-auto,
//
// But in reality, they only supports the following.
export type Recapp2SpeechToTextCode = 'gsw-CH';

// Based on supported languages in https://cloud.google.com/translate/docs/languages
export const supportedTranslationLanguageMap: {[key: string]: string} = {
  'af': 'Afrikaans',
  'sq': 'Albanian',
  'am': 'Amharic',
  'amc': 'Amhari',
  'ar': 'Arabic',
  'hy': 'Armenian',
  'az': 'Azerbaijani',
  'eu': 'Basque',
  'be':'Belarusian',
  'bn':'Bengali',
  'bs':'Bosnian',
  'bg':'Bulgarian',
  'ca':'Catalan',
  'ceb':'Cebuano',
  'zh':'Chinese (Simplified)',
  'zh-TW':'Chinese (Traditional)',
  'co':'Corsican',
  'hr':'Croatian',
  'cs':'Czech',
  'da':'Danish',
  'nl':'Dutch',
  'en':'English',
  'eo':'Esperanto',
  'et':'Estonian',
  'fi':'Finnish',
  'fr':'French',
  'fy':'Frisian',
  'gl':'Galician',
  'ka':'Georgian',
  'de':'German',
  'el':'Greek',
  'gu':'Gujarati',
  'ht':'Haitian Creole',
  'ha':'Hausa',
  'haw':'Hawaiian',
  'he':'Hebrew',
  'hi':'Hindi',
  'hmn':'Hmong',
  'hu':'Hungarian',
  'is':'Icelandic',
  'ig':'Igbo',
  'id':'Indonesian',
  'ga':'Irish',
  'it':'Italian',
  'ja':'Japanese',
  'jv':'Javanese',
  'kn':'Kannada',
  'kk':'Kazakh',
  'km':'Khmer',
  'ko':'Korean',
  'ku':'Kurdish',
  'ky':'Kyrgyz',
  'lo':'Lao',
  'la':'Latin',
  'lv':'Latvian',
  'lt':'Lithuanian',
  'lb':'Luxembourgish',
  'mk':'Macedonian',
  'mg':'Malagasy',
  'ms':'Malay',
  'ml':'Malayalam',
  'mt':'Maltese',
  'mi': 'Maori',
  'mr':'Marathi',
  'mn':'Mongolian',
  'my':'Myanmar (Burmese)',
  'ne':'Nepali',
  'no':'Norwegian',
  'ny':'Nyanja (Chicewa)',
  'ps':'Pashto',
  'fa':'Persian',
  'pl':'Polish',
  'pt':'Portuguese (Portugal, Brazil)',
  'pa':'Punjabi',
  'ro':'Romanian',
  'ru':'Russian',
  'sm':'Samoan',
  'gd':'Scots Gaelic',
  'sr':'Serbian',
  'st':'Sesotho',
  'sn':'Shona',
  'sd':'Sindhi',
  'si':'Sinhala (Sinhalese)',
  'sk':'Slovak',
  'sl':'Slovenian',
  'so':'Somali',
  'es':'Spanish',
  'su':'Sundanese',
  'sw':'Swahili',
  'sv':'Swedish',
  'tl':'Tagalog (Filipino)',
  'tg':'Tajik',
  'ta':'Tamil',
  'te':'Telugu',
  'th':'Thai',
  'tr':'Turkish',
  'uk':'Ukrainian',
  'ur':'Urdu',
  'uz':'Uzbek',
  'vi':'Vietnamese',
  'cy':'Welsh',
  'xh':'Xhosa',
  'yi':'Yiddish',
  'yo':'Yoruba',
  'zu':'Zulu'
};

// All the ISO 639-1 language codes
const iso639_1Codes = <const> [
  'aa', 'ab', 'ae', 'af', 'ak', 'am', 'an', 'ar', 'as', 'av', 'ay', 'az', 'ba', 'be',
  'bg', 'bh', 'bi', 'bm', 'bn', 'bo', 'br', 'bs', 'ca', 'ce', 'ch', 'co', 'cr', 'cs',
  'cu', 'cv', 'cy', 'da', 'de', 'dv', 'dz', 'ee', 'el', 'en', 'eo', 'es', 'et', 'eu',
  'fa', 'ff', 'fi', 'fj', 'fo', 'fr', 'fy', 'ga', 'gd', 'gl', 'gn', 'gu', 'gv', 'ha',
  'he', 'hi', 'ho', 'hr', 'ht', 'hu', 'hy', 'hz', 'ia', 'id', 'ie', 'ig', 'ii', 'ik',
  'io', 'is', 'it', 'iu', 'ja', 'jv', 'ka', 'kg', 'hi', 'kj', 'kk', 'kl', 'km', 'kn',
  'ko', 'kr', 'ks', 'ku', 'kv', 'kw', 'ky', 'la', 'lb', 'lg', 'li', 'ln', 'lo', 'lt',
  'lu', 'lv', 'mg', 'mh', 'mi', 'mk', 'ml', 'mn', 'mr', 'ms', 'mt', 'my', 'na', 'nb',
  'nd', 'ne', 'ng', 'nl', 'nn', 'no', 'nr', 'nv', 'ny', 'oc', 'oj', 'om', 'or', 'os',
  'pa', 'pi', 'pl', 'ps', 'pt', 'qu', 'rm', 'rn', 'ro', 'ru', 'rw', 'sa', 'sc', 'sd',
  'se', 'sg', 'si', 'sk', 'sl', 'sm', 'sn', 'so', 'sq', 'sr', 'ss', 'st', 'su', 'sv',
  'sw', 'ta', 'te', 'tg', 'th', 'ti', 'tk', 'tl', 'tn', 'to', 'tr', 'ts', 'tt', 'tw',
  'ty', 'ug', 'uk', 'ur', 'uz', 've', 'vi', 'vo', 'wa', 'wo', 'xh', 'yi', 'yo', 'za',
  'zh', 'zu', 'auto'];
export type Iso639_1 = typeof iso639_1Codes[number];

export const isIso639_1 = (value: string): value is Iso639_1 => {
  return iso639_1Codes.indexOf(value as Iso639_1) > -1;
};

export interface SupportedLanguage {
  languageCode: LanguageCode;
  iso639_1Code: Iso639_1;

  // A human-readable description of the language
  label: string;

  textrazorCode?: TextRazorCode;
  awsTranscribeCode?: AwsTranscribeCode;
  googleSpeechToTextCode?: GoogleSpeechToTextCode;
  ibmSpeechToTextCode?: IbmSpeechToTextCode;
  ibmNluCode?: IbmNluCode;
  msSpeechToTextCode?: MsSpeechToTextCode;
  zoomSpeechToTextCode?: ZoomSpeechToTextCode;
  speechmaticsSpeechToTextCode?: SpeechmaticsSpeechToTextCode,
  trintSpeechToTextCode?: TrintSpeechToTextCode,
  amberscriptSpeechToTextCode?: AmberscriptSpeechToTextCode,
  recapp2SpeechToTextCode?: Recapp2SpeechToTextCode,

  // Specifies whether it should be shown for a new unconfigured tenant
  defaultList: boolean;
}

export const getSupportedLanguage = (allLanguages: SupportedLanguage[], languageCode: LanguageCode): SupportedLanguage | undefined => {
  if (!languageCode) {
    return undefined;
  }
  for (const lang of allLanguages) {
    if (lang.languageCode === languageCode) {
      return lang;
    }
  }
  return undefined;
};

export const isRightToLeftLanguage = (allLanguages: SupportedLanguage[], languageCode: LanguageCode): boolean => {
  const l = getSupportedLanguage(allLanguages, languageCode);
  if (!l) {
    return false;
  }
  return [
    'ar', // Arabic
    'he', // Hebrew
    'dv', // Divehi
    'ff', // Fulah
    'ku', // Kurdish
    'fa', // Persian
    'ur', // Urdu
    'ug', // Uyghur
  ].includes(l.iso639_1Code);
};

// Given a language and a speech-to-text service, returns the language code if the given
// service is supported and undefined otherwise
export const getSpeechToTextServiceCode = (language: SupportedLanguage, service: string): string | undefined => {
  switch(service) {
    case 'awsTranscribe': return language.awsTranscribeCode;
    case 'googleSpeechToText': return language.googleSpeechToTextCode;
    case 'ibmSpeechToText': return language.ibmSpeechToTextCode;
    case 'msSpeechToText': return language.msSpeechToTextCode;
    case 'zoomSpeechToText': return language.zoomSpeechToTextCode;
    case 'speechmaticsSpeechToText': return language.speechmaticsSpeechToTextCode;
    case 'trintSpeechToText': return language.trintSpeechToTextCode;
    case 'amberscriptSpeechToText': return language.amberscriptSpeechToTextCode;
    case 'recapp2SpeechToText': return language.recapp2SpeechToTextCode;
    default: return undefined;
  }
};

export const isSupportedSpeechToTextService = (language: SupportedLanguage, service: string): boolean => {
  const code = getSpeechToTextServiceCode(language, service);
  return !isEmpty(code);
};

// Returns the human-readable name of a transcription service to be displayed in the UI
export const getSpeechToTextServiceName = (service: string): string | undefined => {
  switch(service) {
    case 'awsTranscribe': return 'AWS';
    case 'googleSpeechToText': return 'Google';
    case 'ibmSpeechToText': return 'IBM';
    case 'msSpeechToText': return 'Microsoft';
    case 'zoomSpeechToText': return 'Scriptix';
    case 'speechmaticsSpeechToText': return 'Speechmatics';
    case 'trintSpeechToText': return 'Trint';
    case 'amberscriptSpeechToText': return 'Amberscript';
    case 'recapp2SpeechToText': return 'Recapp';
    default: return undefined;
  }
};
